/**
 * Dialog Flow Configuration
 * 
 * Configuration for visualizing dialog nodes and their flow connections.
 * Each DialogNode becomes a visual flow node, with edges representing connections.
 */

import { Node, Edge, NodeTypes, EdgeTypes } from '@xyflow/react';
import { FlowConfig } from '../types';
import { Dialog, DialogNode, getCachedDialogs } from '@/lib/stores/dialogStore';
import { DialogFieldNode } from '../nodes/DialogFieldNode';
import { ExternalDialogNode } from '../nodes/ExternalDialogNode';
import { InteractiveEdge } from '../edges/InteractiveEdge';
import { autoLayoutNodes, generateNodeId, generateEdgeId } from '../utils';
import { getTypeItemById } from '@/lib/utils/typeStoreResolver';

const nodeTypes: NodeTypes = {
  field: DialogFieldNode,
  externalDialog: ExternalDialogNode,
};

// Custom edge types with interactive controls
const edgeTypes: EdgeTypes = {
  default: InteractiveEdge,
  straight: InteractiveEdge,
  step: InteractiveEdge,
  smoothstep: InteractiveEdge,
  simplebezier: InteractiveEdge,
};

/**
 * Generate a unique node ID for flow visualization
 */
function getNodeId(nodeIndex: number, nodeId?: string): string {
  return `node-${nodeIndex}-${nodeId || 'new'}`;
}

/**
 * Generate a unique external dialog node ID
 * Format: external-dialog-{UUID}-section-{nodeIdentifier}
 */
function getExternalDialogNodeId(dialogId: string, nodeIdentifier?: string): string {
  return `external-dialog-${dialogId}${nodeIdentifier ? `-section-${nodeIdentifier}` : ''}`;
}

export const dialogFlowConfig: FlowConfig<Dialog> = {
  itemType: 'dialog',
  
  nodeTypes,
  edgeTypes,
  
  /**
   * Transform dialog data to nodes
   * Each DialogNode becomes a visual flow node, plus external dialog nodes for cross-dialog references
   */
  dataToNodes: (dialog: Dialog) => {
    const dialogNodes = dialog.data?.nodes || [];
    const nodes: Node[] = [];
    
    // Track external dialogs referenced in this dialog
    const externalDialogRefs = new Map<string, { dialogId: string; nodeIdentifier?: string }>();
    
    // First pass: collect all external dialog references
    dialogNodes.forEach((dialogNode) => {
      // Check for external dialog references in options
      if (dialogNode.type === 'question' && dialogNode.options) {
        dialogNode.options.forEach(option => {
          if (option.next_dialog) {
            const key = `${option.next_dialog}-${option.next_dialog_node || option.next_dialog_section || ''}`;
            if (!externalDialogRefs.has(key)) {
              externalDialogRefs.set(key, {
                dialogId: option.next_dialog,
                nodeIdentifier: option.next_dialog_node || option.next_dialog_section || undefined,
              });
            }
          }
        });
      }
      
      // Check for external dialog references in direct navigation
      // This includes 'external-dialog' type nodes and any other nodes with next_dialog
      if (dialogNode.next_dialog) {
        const key = `${dialogNode.next_dialog}-${dialogNode.next_dialog_node || dialogNode.next_dialog_section || ''}`;
        if (!externalDialogRefs.has(key)) {
          externalDialogRefs.set(key, {
            dialogId: dialogNode.next_dialog,
            nodeIdentifier: dialogNode.next_dialog_node || dialogNode.next_dialog_section || undefined,
          });
        }
      }
      
      // Check for external dialog references in condition options
      if (dialogNode.conditionOptions) {
        dialogNode.conditionOptions.forEach(condOption => {
          if (condOption.next_dialog) {
            const key = `${condOption.next_dialog}-${condOption.next_dialog_node || condOption.next_dialog_section || ''}`;
            if (!externalDialogRefs.has(key)) {
              externalDialogRefs.set(key, {
                dialogId: condOption.next_dialog,
                nodeIdentifier: condOption.next_dialog_node || condOption.next_dialog_section || undefined,
              });
            }
          }
        });
      }
    });
    
    // Second pass: create visual nodes for each DialogNode (except external-dialog which are invisible routing points)
    dialogNodes.forEach((dialogNode, nodeIndex) => {
      const flowNodeId = getNodeId(nodeIndex, dialogNode.id);
      
      // Skip creating visual nodes for external-dialog fields
      // They act as invisible routing points that redirect edges to external dialog references
      if (dialogNode.type === 'external-dialog') {
        return;
      }
      
      nodes.push({
        id: flowNodeId,
        type: 'field',
        position: { x: 0, y: 0 }, // Will be auto-laid out
        data: {
          node: dialogNode,
          field: dialogNode, // Keep for backward compatibility
          nodeName: dialogNode.name || `Node ${nodeIndex + 1}`,
          sectionName: dialogNode.name || `Node ${nodeIndex + 1}`, // Legacy
          nodeIndex,
          sectionIndex: nodeIndex, // Legacy - for backward compatibility with onSectionUpdate
          fieldIndex: 0, // Legacy
          label: dialogNode.text?.substring(0, 50) || dialogNode.type,
          nodes: dialogNodes.map(n => ({ node_identifier: n.node_identifier, name: n.name })),
          sections: dialogNodes.map(n => ({ section_identifier: n.node_identifier, name: n.name })), // Legacy
          variables: dialog.data?.variables || [],
        },
      });
    });
    
    // Create external dialog nodes
    const allDialogs = dialog.game_id ? getCachedDialogs(dialog.game_id) : [];
    
    externalDialogRefs.forEach(({ dialogId, nodeIdentifier }) => {
      const targetDialog = allDialogs.find(d => d.id === dialogId);
      const targetNode = targetDialog?.data?.nodes?.find(n => n.node_identifier === nodeIdentifier);
      
      nodes.push({
        id: getExternalDialogNodeId(dialogId, nodeIdentifier),
        type: 'externalDialog',
        position: { x: 0, y: 0 }, // Will be auto-laid out
        data: {
          dialogId,
          dialogName: targetDialog?.name || 'Unknown Dialog',
          dialogKey: targetDialog?.dialog_key,
          targetNode: nodeIdentifier,
          targetSection: nodeIdentifier, // Legacy
          nodeName: targetNode?.name,
          sectionName: targetNode?.name, // Legacy
        },
      });
    });
    
    // Apply auto-layout
    const edges = dialogFlowConfig.dataToEdges(dialog);
    return autoLayoutNodes(nodes, edges, 'LR'); // Left to right layout
  },
  
  /**
   * Transform dialog data to edges
   * Creates edges between dialog nodes based on flow logic
   */
  dataToEdges: (dialog: Dialog) => {
    const dialogNodes = dialog.data?.nodes || [];
    const edges: Edge[] = [];
    const dialogVariables = dialog.data?.variables || [];
    
    // Get stored edge styles from flow metadata
    const edgeStyles = dialog.data?.flowMetadata?.edgeStyles || {};
    
    // Helper function to apply stored edge styles
    const applyStoredStyles = (edge: Edge): Edge => {
      const stored = edgeStyles[edge.id];
      if (stored) {
        return {
          ...edge,
          type: stored.type || edge.type,
          animated: stored.animated !== undefined ? stored.animated : edge.animated,
        };
      }
      return edge;
    };
    
    // Helper function to generate label for condition options
    const getConditionOptionLabel = (condOption: any, dialogNode: DialogNode): string => {
      // For range-selector with in_range operator, show the range name
      if (condOption.operator === 'in_range' && dialogNode.condition) {
        const variable = dialogVariables.find(v => v.name === dialogNode.condition?.variable);
        if (variable?.type === 'range-selector' && variable.typeStoreRef?.typeId) {
          const typeItem = getTypeItemById(variable.typeStoreRef.typeId);
          const rangeElement = typeItem?.data?.find(el => el.id === String(condOption.condition_value));
          if (rangeElement) {
            return `in ${rangeElement.name}`;
          }
        }
      }
      
      // Default label format
      return `${condOption.operator || '=='} ${String(condOption.condition_value)}`;
    };
    
    // Build a map of node identifiers to their flow node IDs
    // Also track external-dialog nodes for edge resolution
    const nodeIdentifierToFlowNodeId = new Map<string, string>();
    const externalDialogNodes = new Map<string, DialogNode>();
    
    dialogNodes.forEach((dialogNode, nodeIndex) => {
      const flowNodeId = getNodeId(nodeIndex, dialogNode.id);
      nodeIdentifierToFlowNodeId.set(dialogNode.node_identifier, flowNodeId);
      
      // Track external-dialog nodes separately for edge routing
      if (dialogNode.type === 'external-dialog') {
        externalDialogNodes.set(dialogNode.node_identifier, dialogNode);
      }
    });
    
    // Helper function to resolve a target node identifier
    // If target is an external-dialog node, return the external dialog reference ID
    // Otherwise return the normal flow node ID
    const resolveTargetNode = (targetIdentifier: string): { id: string; isExternal: boolean } | null => {
      // Check if target is an external-dialog node (invisible routing point)
      const externalDialogNode = externalDialogNodes.get(targetIdentifier);
      if (externalDialogNode && externalDialogNode.next_dialog) {
        // Route through to the actual external dialog reference node
        return {
          id: getExternalDialogNodeId(
            externalDialogNode.next_dialog,
            externalDialogNode.next_dialog_node || externalDialogNode.next_dialog_section || undefined
          ),
          isExternal: true
        };
      }
      
      // Normal node
      const flowNodeId = nodeIdentifierToFlowNodeId.get(targetIdentifier);
      return flowNodeId ? { id: flowNodeId, isExternal: false } : null;
    };
    
    dialogNodes.forEach((dialogNode, nodeIndex) => {
      const sourceFlowNodeId = getNodeId(nodeIndex, dialogNode.id);
      
      // Skip end-dialog and external-dialog nodes (no outgoing edges from them)
      // External-dialog nodes are invisible routing points
      if (dialogNode.type === 'end-dialog' || dialogNode.type === 'external-dialog') {
        return;
      }
      
      // Handle condition nodes
      if (dialogNode.type === 'condition') {
        // Check for new conditionOptions format first
        if (dialogNode.conditionOptions && dialogNode.conditionOptions.length > 0) {
          dialogNode.conditionOptions.forEach((condOption, optionIndex) => {
            const nextNode = condOption.next_node || condOption.next_section;
            if (nextNode) {
              const resolved = resolveTargetNode(nextNode);
              
              if (resolved) {
                edges.push(applyStoredStyles({
                  id: `${sourceFlowNodeId}-condopt${optionIndex}-${resolved.id}`,
                  source: sourceFlowNodeId,
                  sourceHandle: `cond-option-${optionIndex}`,
                  target: resolved.id,
                  targetHandle: 'left',
                  type: 'default',
                  animated: true,
                  style: resolved.isExternal 
                    ? { stroke: '#f97316', strokeWidth: 2, strokeDasharray: '5,5' }
                    : { stroke: '#8b5cf6', strokeWidth: 2 },
                  label: getConditionOptionLabel(condOption, dialogNode),
                  data: {
                    type: resolved.isExternal ? 'condition-option-external' : 'condition-option',
                    optionIndex,
                  },
                }));
              }
            } else if (condOption.next_dialog) {
              const externalNodeId = getExternalDialogNodeId(
                condOption.next_dialog, 
                condOption.next_dialog_node || condOption.next_dialog_section || undefined
              );
              edges.push(applyStoredStyles({
                id: `${sourceFlowNodeId}-condopt${optionIndex}-${externalNodeId}`,
                source: sourceFlowNodeId,
                sourceHandle: `cond-option-${optionIndex}`,
                target: externalNodeId,
                targetHandle: 'left',
                type: 'default',
                animated: true,
                style: { stroke: '#f97316', strokeWidth: 2, strokeDasharray: '5,5' },
                label: getConditionOptionLabel(condOption, dialogNode),
                data: {
                  type: 'condition-option-external',
                  optionIndex,
                },
              }));
            }
          });
          
          return; // Skip legacy handling if conditionOptions exist
        }
        
        // Legacy true/false branch handling
        const trueTarget = dialogNode.next_section_true;
        if (trueTarget) {
          const resolved = resolveTargetNode(trueTarget);
          if (resolved) {
            edges.push(applyStoredStyles({
              id: `${sourceFlowNodeId}-true-${resolved.id}`,
              source: sourceFlowNodeId,
              sourceHandle: 'true',
              target: resolved.id,
              targetHandle: 'left',
              type: 'default',
              animated: true,
              style: resolved.isExternal 
                ? { stroke: '#22c55e', strokeWidth: 2, strokeDasharray: '5,5' }
                : { stroke: '#22c55e', strokeWidth: 2 },
              label: '✓ true',
              data: {
                type: resolved.isExternal ? 'condition-true-external' : 'condition-true',
              },
            }));
          }
        } else if (dialogNode.next_dialog_true) {
          const externalNodeId = getExternalDialogNodeId(
            dialogNode.next_dialog_true, 
            dialogNode.next_dialog_section_true || undefined
          );
          edges.push(applyStoredStyles({
            id: `${sourceFlowNodeId}-true-${externalNodeId}`,
            source: sourceFlowNodeId,
            sourceHandle: 'true',
            target: externalNodeId,
            targetHandle: 'left',
            type: 'default',
            animated: true,
            style: { stroke: '#22c55e', strokeWidth: 2, strokeDasharray: '5,5' },
            label: '✓ true',
            data: {
              type: 'condition-true-external',
            },
          }));
        }
        
        // False branch
        const falseTarget = dialogNode.next_section_false;
        if (falseTarget) {
          const resolved = resolveTargetNode(falseTarget);
          if (resolved) {
            edges.push(applyStoredStyles({
              id: `${sourceFlowNodeId}-false-${resolved.id}`,
              source: sourceFlowNodeId,
              sourceHandle: 'false',
              target: resolved.id,
              targetHandle: 'left',
              type: 'default',
              animated: true,
              style: resolved.isExternal 
                ? { stroke: '#ef4444', strokeWidth: 2, strokeDasharray: '5,5' }
                : { stroke: '#ef4444', strokeWidth: 2 },
              label: '✗ false',
              data: {
                type: resolved.isExternal ? 'condition-false-external' : 'condition-false',
              },
            }));
          }
        } else if (dialogNode.next_dialog_false) {
          const externalNodeId = getExternalDialogNodeId(
            dialogNode.next_dialog_false, 
            dialogNode.next_dialog_section_false || undefined
          );
          edges.push(applyStoredStyles({
            id: `${sourceFlowNodeId}-false-${externalNodeId}`,
            source: sourceFlowNodeId,
            sourceHandle: 'false',
            target: externalNodeId,
            targetHandle: 'left',
            type: 'default',
            animated: true,
            style: { stroke: '#ef4444', strokeWidth: 2, strokeDasharray: '5,5' },
            label: '✗ false',
            data: {
              type: 'condition-false-external',
            },
          }));
        }
      }
      // Handle question nodes with options
      else if (dialogNode.type === 'question' && dialogNode.options && dialogNode.options.length > 0) {
        dialogNode.options.forEach((option, optionIndex) => {
          const nextNode = option.next_node || option.next_section;
          if (nextNode) {
            const resolved = resolveTargetNode(nextNode);
            
            if (resolved) {
              edges.push(applyStoredStyles({
                id: `${sourceFlowNodeId}-opt${optionIndex}-${resolved.id}`,
                source: sourceFlowNodeId,
                sourceHandle: `option-${optionIndex}`,
                target: resolved.id,
                targetHandle: 'left',
                type: 'default',
                animated: true,
                style: resolved.isExternal 
                  ? { stroke: '#f97316', strokeWidth: 2, strokeDasharray: '5,5' }
                  : { stroke: '#8b5cf6', strokeWidth: 2 },
                label: option.option_text?.substring(0, 20) || `Option ${optionIndex + 1}`,
                data: {
                  label: option.option_text?.substring(0, 30),
                  type: resolved.isExternal ? 'external' : 'branch',
                  optionIndex,
                },
              }));
            }
          } else if (option.next_dialog) {
            const externalNodeId = getExternalDialogNodeId(
              option.next_dialog, 
              option.next_dialog_node || option.next_dialog_section || undefined
            );
            edges.push(applyStoredStyles({
              id: `${sourceFlowNodeId}-opt${optionIndex}-${externalNodeId}`,
              source: sourceFlowNodeId,
              sourceHandle: `option-${optionIndex}`,
              target: externalNodeId,
              targetHandle: 'left',
              type: 'default',
              animated: true,
              style: { stroke: '#f97316', strokeWidth: 2, strokeDasharray: '5,5' },
              label: option.option_text?.substring(0, 20) || `Option ${optionIndex + 1}`,
              data: {
                label: option.option_text?.substring(0, 30),
                type: 'external',
                optionIndex,
              },
            }));
          }
        });
      } else {
        // Non-question nodes: check for node-level navigation
        const nextNode = dialogNode.next_node || dialogNode.next_section;
        
        // Determine the correct source handle based on node type
        // Setter nodes use 'next' handle, other nodes use 'right' handle
        const sourceHandle = dialogNode.type === 'setter' ? 'next' : 'right';
        
        if (nextNode) {
          const resolved = resolveTargetNode(nextNode);
          
          if (resolved) {
            edges.push(applyStoredStyles({
              id: `${sourceFlowNodeId}-jump-${resolved.id}`,
              source: sourceFlowNodeId,
              sourceHandle: sourceHandle,
              target: resolved.id,
              targetHandle: 'left',
              type: 'default',
              animated: true,
              style: resolved.isExternal 
                ? { stroke: '#f97316', strokeWidth: 2, strokeDasharray: '5,5' }
                : { stroke: '#8b5cf6', strokeWidth: 2 },
              data: {
                type: resolved.isExternal ? 'node-external-jump' : 'node-jump',
              },
            }));
          }
        } else if (dialogNode.next_dialog) {
          const externalNodeId = getExternalDialogNodeId(
            dialogNode.next_dialog, 
            dialogNode.next_dialog_node || dialogNode.next_dialog_section || undefined
          );
          edges.push(applyStoredStyles({
            id: `${sourceFlowNodeId}-jump-${externalNodeId}`,
            source: sourceFlowNodeId,
            sourceHandle: sourceHandle,
            target: externalNodeId,
            targetHandle: 'left',
            type: 'default',
            animated: true,
            style: { stroke: '#f97316', strokeWidth: 2, strokeDasharray: '5,5' },
            data: {
              type: 'node-external-jump',
            },
          }));
        }
      }
    });
    
    return edges;
  },
  
  /**
   * Transform nodes and edges back to dialog data
   * (Optional - not implemented for read-only visualization)
   */
  nodesToData: (nodes: Node[], edges: Edge[]) => {
    // This would require complex logic to reconstruct the dialog structure
    // For now, we'll keep this as a visualization-only feature
    console.warn('dialogFlowConfig.nodesToData: Not implemented - dialog flow is read-only');
    return {} as Dialog;
  },
  
  /**
   * Validate connections
   * For dialog flows, we allow connections between nodes
   */
  validateConnection: (connection) => {
    // Basic validation - can be extended
    if (connection.source === connection.target) {
      return {
        valid: false,
        message: 'Cannot connect a node to itself'
      };
    }
    
    return { valid: true };
  },
  
  /**
   * Validate drop - dialog flows are read-only, no drops allowed
   */
  validateDrop: (dragData: any) => {
    return {
      valid: false,
      message: 'Dialog flow visualization is read-only. Edit the dialog in the dialog editor.'
    };
  },
};

export default dialogFlowConfig;

